import React, { useEffect, useRef, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { Plus, Trash2, FileSpreadsheet, Camera, Upload, X, Image as ImageIcon } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function MRDRS() {
    const [fields, setFields] = useState([]);
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
    const [entries, setEntries] = useState([]);
    const [history, setHistory] = useState([]);
    const [busy, setBusy] = useState(false);
    const [uploadingFor, setUploadingFor] = useState(null);

    const load = async () => {
        const [f, h] = await Promise.all([api.get("/drs-fields"), api.get("/mr/drs")]);
        setFields(f.data); setHistory(h.data);
        if (entries.length === 0) addRow(f.data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const addRow = (fs = fields) => {
        const blank = {}; (fs || []).forEach(f => { blank[f.key] = f.type === "toggle" ? false : ""; });
        if (blank.visit_date !== undefined) blank.visit_date = date;
        setEntries(cur => [...cur, { values: blank, attachments: [] }]);
    };
    const updateCell = (rowIdx, key, val) => setEntries(cur => cur.map((r, i) => i === rowIdx ? { ...r, values: { ...r.values, [key]: val } } : r));
    const removeRow = (rowIdx) => setEntries(cur => cur.filter((_, i) => i !== rowIdx));

    const uploadVisitPhoto = async (rowIdx, file) => {
        if (!file) return;
        setUploadingFor(rowIdx);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post("/mr/drs/visit-photo", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 60000,
            });
            setEntries(cur => cur.map((r, i) =>
                i === rowIdx ? { ...r, attachments: [...(r.attachments || []), data.url] } : r
            ));
            toast.success("Photo attached");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Upload failed");
        } finally { setUploadingFor(null); }
    };
    const removeAttachment = (rowIdx, urlIdx) => setEntries(cur => cur.map((r, i) =>
        i === rowIdx ? { ...r, attachments: (r.attachments || []).filter((_, j) => j !== urlIdx) } : r
    ));

    const submit = async () => {
        if (entries.length === 0) return toast.error("Add at least one visit");
        setBusy(true);
        try {
            // Strip empty rows (no values, no attachments)
            const cleaned = entries.filter(e =>
                Object.values(e.values).some(v => v !== "" && v !== false) || (e.attachments || []).length > 0
            );
            if (cleaned.length === 0) { toast.error("Fill at least one field"); setBusy(false); return; }
            await api.post("/mr/drs", { date, entries: cleaned });
            toast.success("Daily report submitted");
            setEntries([]); addRow();
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    };

    const apiBase = (api.defaults.baseURL || "").replace(/\/api$/, "");

    return (
        <div data-testid="mr-drs" className="space-y-6">
            <Toaster position="top-center" />
            <div>
                <div className="label-eyebrow text-sky-700">Field log</div>
                <h1 className="font-serif text-4xl text-stone-900 mt-2">Daily Report Sheet</h1>
                <p className="text-sm text-stone-600 mt-2 max-w-xl">Log every client visit. Tap "Add photo" on any row to take a quick snap or attach from gallery — proof-of-visit, prescription pad, sample handover, anything relevant.</p>
            </div>

            <div className="bg-white border border-sky-100 p-4 flex items-center gap-4 flex-wrap">
                <div><Label className="text-xs uppercase tracking-widest text-stone-500">Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-none border-stone-300 mt-1" data-testid="drs-date" /></div>
                <Button onClick={() => addRow()} variant="outline" className="rounded-none border-stone-400 mt-5" data-testid="drs-add-row"><Plus className="w-4 h-4 mr-1" />Add visit</Button>
            </div>

            {entries.map((row, i) => (
                <div key={i} className="bg-white border border-sky-100 p-4 space-y-4" data-testid={`drs-row-${i}`}>
                    <div className="flex items-center justify-between">
                        <div className="text-xs uppercase tracking-widest text-sky-700">Visit {i + 1}</div>
                        <button onClick={() => removeRow(i)} className="text-red-700 text-xs inline-flex items-center" data-testid={`drs-row-remove-${i}`}><Trash2 className="w-3.5 h-3.5 mr-1" />Remove visit</button>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {fields.map(f => (
                            <div key={f.id}>
                                <Label className="text-[10px] uppercase tracking-widest text-stone-500">{f.label}{f.required && <span className="text-red-500 ml-1">*</span>}</Label>
                                <div className="mt-1">
                                    <FieldInput field={f} value={row.values[f.key]} onChange={(v) => updateCell(i, f.key, v)} />
                                </div>
                            </div>
                        ))}
                    </div>

                    <DRSPhotoStrip
                        rowIdx={i}
                        attachments={row.attachments || []}
                        apiBase={apiBase}
                        uploading={uploadingFor === i}
                        onPick={(file) => uploadVisitPhoto(i, file)}
                        onRemove={(j) => removeAttachment(i, j)}
                    />
                </div>
            ))}

            <Button onClick={submit} disabled={busy} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="drs-submit"><FileSpreadsheet className="w-4 h-4 mr-2" />{busy ? "Submitting…" : "Submit DRS"}</Button>

            <div>
                <h2 className="font-serif text-2xl mt-8 mb-4">Past reports</h2>
                {history.length === 0 ? <p className="text-sm text-stone-500">None yet.</p> : (
                    <div className="bg-white border border-sky-100">
                        {history.map(h => {
                            const photoCount = (h.entries || []).reduce((sum, e) => sum + (e.attachments?.length || 0), 0);
                            return (
                                <div key={h.id} className="p-4 border-b border-stone-100 last:border-b-0" data-testid={`drs-hist-${h.id}`}>
                                    <div className="flex justify-between flex-wrap gap-2">
                                        <div className="font-medium">{h.date}</div>
                                        <div className="text-xs text-stone-500">{h.entries.length} visits · {photoCount} photo{photoCount === 1 ? "" : "s"}</div>
                                    </div>
                                    {photoCount > 0 && (
                                        <div className="flex gap-2 mt-3 flex-wrap">
                                            {h.entries.flatMap(e => e.attachments || []).map((u, k) => (
                                                <a key={k} href={`${apiBase}${u}`} target="_blank" rel="noreferrer" className="block w-16 h-16 border border-sky-100 overflow-hidden">
                                                    <img src={`${apiBase}${u}`} alt={`visit ${k}`} className="w-full h-full object-cover" loading="lazy" />
                                                </a>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

function DRSPhotoStrip({ rowIdx, attachments, apiBase, uploading, onPick, onRemove }) {
    const cameraRef = useRef(null);
    const galleryRef = useRef(null);
    const fileRef = useRef(null);

    const isImage = (url) => /\.(jpe?g|png|webp|gif|heic|heif)(\?|$)/i.test(url);
    const filename = (url) => url.split("/").pop().split("?")[0];

    return (
        <div className="border-t border-stone-100 pt-3">
            <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-2 flex items-center gap-1">
                <ImageIcon className="w-3 h-3" />
                Visit attachments {attachments.length > 0 && <span className="text-sky-700">({attachments.length})</span>}
            </div>
            <div className="flex gap-2 flex-wrap items-center">
                {attachments.map((u, j) => (
                    <div key={j} className="relative group" data-testid={`drs-photo-${rowIdx}-${j}`}>
                        {isImage(u) ? (
                            <a href={`${apiBase}${u}`} target="_blank" rel="noreferrer">
                                <img src={`${apiBase}${u}`} alt={`visit ${j}`} className="w-16 h-16 object-cover border border-sky-100" />
                            </a>
                        ) : (
                            <a href={`${apiBase}${u}`} target="_blank" rel="noreferrer" className="w-16 h-16 border border-sky-100 bg-sky-50 flex flex-col items-center justify-center text-[9px] text-sky-700 p-1 text-center hover:bg-sky-100" title={filename(u)}>
                                <ImageIcon className="w-3 h-3 mb-1" />
                                <span className="truncate w-full">{filename(u).slice(-12)}</span>
                            </a>
                        )}
                        <button type="button" onClick={() => onRemove(j)} className="absolute -top-1 -right-1 bg-white border border-stone-300 hover:border-red-700 hover:text-red-700 text-stone-600 p-0.5 opacity-0 group-hover:opacity-100" aria-label="Remove">
                            <X className="w-3 h-3" />
                        </button>
                    </div>
                ))}
                <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
                    onChange={(e) => { onPick(e.target.files?.[0] || null); e.target.value = ""; }}
                    data-testid={`drs-camera-input-${rowIdx}`} />
                <input ref={galleryRef} type="file" accept="image/*" className="hidden"
                    onChange={(e) => { onPick(e.target.files?.[0] || null); e.target.value = ""; }}
                    data-testid={`drs-gallery-input-${rowIdx}`} />
                <input ref={fileRef} type="file"
                    accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,text/csv"
                    className="hidden"
                    onChange={(e) => { onPick(e.target.files?.[0] || null); e.target.value = ""; }}
                    data-testid={`drs-file-input-${rowIdx}`} />
                <Button type="button" variant="outline" disabled={uploading} onClick={() => cameraRef.current?.click()}
                    className="rounded-none border-sky-700 text-sky-700 hover:bg-sky-50 h-16" data-testid={`drs-camera-${rowIdx}`}>
                    <div className="flex flex-col items-center text-xs gap-1"><Camera className="w-4 h-4" />Take photo</div>
                </Button>
                <Button type="button" variant="outline" disabled={uploading} onClick={() => galleryRef.current?.click()}
                    className="rounded-none border-stone-400 h-16" data-testid={`drs-gallery-${rowIdx}`}>
                    <div className="flex flex-col items-center text-xs gap-1"><ImageIcon className="w-4 h-4" />Gallery</div>
                </Button>
                <Button type="button" variant="outline" disabled={uploading} onClick={() => fileRef.current?.click()}
                    className="rounded-none border-stone-400 h-16" data-testid={`drs-file-${rowIdx}`}>
                    <div className="flex flex-col items-center text-xs gap-1"><Upload className="w-4 h-4" />Document</div>
                </Button>
                {uploading && <span className="text-xs text-stone-500">Uploading…</span>}
            </div>
            <p className="text-[10px] text-stone-500 mt-2">Photos, PDFs, DOC/DOCX, XLS/XLSX, TXT/CSV up to 10 MB.</p>
        </div>
    );
}

function FieldInput({ field, value, onChange }) {
    if (field.type === "textarea") return <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} className="rounded-none border-stone-300" rows={2} />;
    if (field.type === "number") return <Input type="number" value={value || ""} onChange={(e) => onChange(e.target.value)} className="rounded-none border-stone-300" />;
    if (field.type === "date") return <Input type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} className="rounded-none border-stone-300" />;
    if (field.type === "toggle") return <div className="pt-1"><Switch checked={!!value} onCheckedChange={onChange} /></div>;
    if (field.type === "select") return (
        <Select value={value || ""} onValueChange={onChange}>
            <SelectTrigger className="rounded-none border-stone-300"><SelectValue placeholder="Choose" /></SelectTrigger>
            <SelectContent>{(field.options || []).map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
    );
    return <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className="rounded-none border-stone-300" />;
}
